import httpRequest from "@/utils/api";
import type {
  User,
  PaginatedUsers,
  UserSearchParams,
  ManuallyActivateSubscriptionResponse,
  UserSubscription,
  UserQuota,
} from "@/types/user/user";
import type { ImpersonateTokenData } from "@/types/admin/impersonate";
import type { UserRole } from "@/types/auth/role";

export interface CreateUserRequest {
  username: string;
  email?: string | null;
  phone?: string | null;
  password: string;
}

/**
 * 管理员退款请求。
 */
export interface RefundUserSubscriptionRequest {
  reason: string;
  note?: string;
}

/**
 * 管理员退款响应数据。
 */
export interface RefundUserSubscriptionData {
  subscriptionId: number;
  paymentStatus: string;
  subscriptionStatus: string;
  tenantCommissionRecordId: number | null;
  tenantCommissionStatus: string | null;
  tenantCommissionAmount: number | null;
  processedAt: string;
}

enum API {
  getUserInfo = "/user/",
  getPaginatedUsers = "/user/pagination",
  searchUsers = "/user/search",
  createUser = "/admin/users",
  impersonateUser = "/admin/impersonate-user/",
  // 角色管理
  updateUserRole = "/admin/users/:userId/role",
  // 订阅管理
  getUserSubscriptions = "/admin/users/:userId/subscriptions",
  createUserSubscription = "/admin/users/:userId/subscriptions",
  cancelUserSubscription = "/admin/users/:userId/subscriptions/:subscriptionId",
  refundUserSubscription = "/admin/users/:userId/subscriptions/:subscriptionId/refund",
  updateSubscriptionEndDate = "/admin/users/:userId/subscriptions/:subscriptionId",
  activateSubscription = "/admin/subscriptions/:subscriptionId/activate",
  // 配额管理
  getUserQuotas = "/admin/users/:userId/quota",
  resetUserQuota = "/admin/users/:userId/quotas/:quotaId/reset",
  resetUserQuotaByFeatureCode = "/admin/users/:userId/quotas/reset",
}

export const getUserInfoAPI = () => {
  return httpRequest.get<User>({
    url: API.getUserInfo,
  });
};

export const getPaginatedUsersAPI = (skip: number, limit: number) => {
  return httpRequest.get<PaginatedUsers>({
    url: API.getPaginatedUsers,
    params: {
      skip,
      limit,
    },
  });
};

export const searchUsersAPI = (params: UserSearchParams) => {
  return httpRequest.get<PaginatedUsers>({
    url: API.searchUsers,
    params,
  });
};

/**
 * 创建真实用户
 *
 * 超级管理员创建用户，会自动创建免费订阅和钱包。
 */
export const createUserAPI = (data: CreateUserRequest) => {
  return httpRequest.post<User>({
    url: API.createUser,
    data,
  });
};



/**
 * 模拟用户登录
 *
 * 生成临时 Token，允许超级管理员以指定用户的身份查看系统
 *
 * @param userId - 要模拟登录的用户 ID
 * @returns 包含临时 Token 的响应对象
 *
 * @throws {403} 权限不足，只有超级管理员才能使用此功能
 * @throws {404} 用户不存在
 *
 * @example
 * ```typescript
 * const response = await impersonateUserAPI(123);
 * const { accessToken } = response.data;
 * window.open(`http://localhost:3000/auth/impersonate-callback?token=${accessToken}`, '_blank');
 * ```
 */
export const impersonateUserAPI = (userId: number) => {
  return httpRequest.post<ImpersonateTokenData>({
    url: `${API.impersonateUser}${userId}`,
  });
};

/**
 * 更新用户角色
 *
 * 管理员更新指定用户的角色（USER、ADMIN、SUPER_ADMIN、TEACHER）
 *
 * @param userId - 用户 ID
 * @param role - 目标角色值（0=USER, 1=SUPER_ADMIN, 2=ADMIN, 3=TEACHER）
 * @returns 更新结果
 *
 * @throws {400} 无效角色值或不能修改自己的角色
 * @throws {403} 权限不足
 * @throws {404} 用户不存在
 */
export const updateUserRoleAPI = (userId: number, role: UserRole) => {
  return httpRequest.patch<void>({
    url: API.updateUserRole.replace(':userId', userId.toString()),
    params: { role },
  });
};

// ============================================================================
// 订阅管理 API
// ============================================================================

/**
 * 获取用户订阅列表
 *
 * 管理员查询指定用户的所有订阅记录
 *
 * @param userId - 用户 ID
 * @returns 用户的订阅列表
 *
 * @example
 * ```typescript
 * const response = await getUserSubscriptionsAPI(123);
 * console.log(response.data.subscriptions);
 * ```
 */
export const getUserSubscriptionsAPI = (userId: number) => {
  return httpRequest.get<{ subscriptions: UserSubscription[]; total: number }>({
    url: API.getUserSubscriptions.replace(':userId', userId.toString()),
  });
};

/**
 * 为用户创建订阅（免费赠送）
 *
 * 管理员为用户创建订阅，自动激活并初始化配额
 *
 * @param userId - 用户 ID
 * @param pricingId - 价格方案 ID
 * @returns 包含订阅信息和配额信息的响应对象
 *
 * @throws {404} 用户不存在或价格方案不存在
 * @throws {400} 用户已有活跃订阅
 *
 * @example
 * ```typescript
 * const response = await createUserSubscriptionAPI(123, 1);
 * console.log(response.data.subscription);
 * console.log(response.data.quotas);
 * ```
 */
export const createUserSubscriptionAPI = (userId: number, pricingId: number) => {
  return httpRequest.post<ManuallyActivateSubscriptionResponse>({
    url: API.createUserSubscription.replace(':userId', userId.toString()),
    params: { pricing_id: pricingId },
  });
};

/**
 * 取消用户订阅
 *
 * 将订阅状态设置为 CANCELLED，但不删除配额记录
 *
 * @param userId - 用户 ID
 * @param subscriptionId - 订阅 ID
 * @returns 空响应
 *
 * @throws {404} 订阅不存在
 * @throws {400} 订阅已取消或已过期
 *
 * @example
 * ```typescript
 * await cancelUserSubscriptionAPI(123, 456);
 * ```
 */
export const cancelUserSubscriptionAPI = (userId: number, subscriptionId: number) => {
  return httpRequest.delete<void>({
    url: API.cancelUserSubscription
      .replace(':userId', userId.toString())
      .replace(':subscriptionId', subscriptionId.toString()),
  });
};

/**
 * 退款并作废订阅订单。
 *
 * 管理员对已支付订阅执行退款，并同步处理未结算的租户佣金。
 *
 * @param userId - 用户 ID
 * @param subscriptionId - 订阅 ID
 * @param data - 退款原因与备注
 * @returns 退款处理结果
 */
export const refundUserSubscriptionAPI = (
  userId: number,
  subscriptionId: number,
  data: RefundUserSubscriptionRequest,
) => {
  return httpRequest.post<RefundUserSubscriptionData>({
    url: API.refundUserSubscription
      .replace(':userId', userId.toString())
      .replace(':subscriptionId', subscriptionId.toString()),
    data,
  });
};

/**
 * 修改订阅到期时间
 *
 * @param userId - 用户 ID
 * @param subscriptionId - 订阅 ID
 * @param newEndDate - 新的到期时间（ISO 8601 格式字符串）
 * @returns 更新后的订阅信息
 *
 * @throws {404} 用户不存在或订阅不存在
 * @throws {400} 订阅不属于该用户、订阅状态不是 ACTIVE、时间验证失败
 *
 * @example
 * ```typescript
 * const response = await updateSubscriptionEndDateAPI(123, 456, '2025-12-31T23:59:59+08:00');
 * if (response.status === 200) {
 *   console.log('订阅到期时间已更新:', response.data);
 * } else {
 *   console.error('更新失败:', response.message);
 * }
 * ```
 */
export const updateSubscriptionEndDateAPI = (
  userId: number,
  subscriptionId: number,
  newEndDate: string
) => {
  return httpRequest.patch<ManuallyActivateSubscriptionResponse>({
    url: API.updateSubscriptionEndDate
      .replace(':userId', userId.toString())
      .replace(':subscriptionId', subscriptionId.toString()),
    data: {
      new_end_date: newEndDate,
    },
  });
};

/**
 * 手动激活订阅
 *
 * 管理员手动激活订阅（免费赠送或特殊情况处理，如支付回调失败）
 *
 * @param subscriptionId - 订阅 ID
 * @returns 激活后的订阅信息
 *
 * @throws {404} 订阅不存在
 * @throws {400} 订阅状态不是 PENDING
 *
 * @example
 * ```typescript
 * const response = await activateSubscriptionAPI(456);
 * if (response.status === 200) {
 *   console.log('订阅已激活:', response.data);
 * } else {
 *   console.error('激活失败:', response.message);
 * }
 * ```
 */
export const activateSubscriptionAPI = (subscriptionId: number) => {
  return httpRequest.post<ManuallyActivateSubscriptionResponse>({
    url: API.activateSubscription.replace(':subscriptionId', subscriptionId.toString()),
  });
};

// ============================================================================
// 配额管理 API
// ============================================================================

/**
 * 获取用户配额列表
 *
 * 管理员查询用户的所有配额记录
 *
 * @param userId - 用户 ID
 * @returns 用户的配额列表
 *
 * @example
 * ```typescript
 * const response = await getUserQuotasAPI(123);
 * console.log(response.data); // UserQuota[]
 * ```
 */
export const getUserQuotasAPI = (userId: number) => {
  return httpRequest.get<UserQuota[]>({
    url: API.getUserQuotas.replace(':userId', userId.toString()),
  });
};

/**
 * 重置用户配额
 *
 * 将 used_count 重置为 0，remaining_count 恢复为 hard_limit
 *
 * @param userId - 用户 ID
 * @param quotaId - 配额 ID
 * @returns 重置后的配额信息
 *
 * @throws {404} 配额不存在
 *
 * @example
 * ```typescript
 * const response = await resetUserQuotaAPI(123, 789);
 * console.log(response.data);
 * ```
 */
export const resetUserQuotaAPI = (userId: number, quotaId: number) => {
  return httpRequest.post<UserQuota>({
    url: API.resetUserQuota
      .replace(':userId', userId.toString())
      .replace(':quotaId', quotaId.toString()),
  });
};

/**
 * 根据功能代码重置用户配额
 *
 * 将 used_count 重置为 0，remaining_count 恢复为 hard_limit
 *
 * @param userId - 用户 ID
 * @param featureCode - 功能代码
 * @returns 重置后的配额信息
 *
 * @throws {404} 配额不存在或功能代码无效
 */
export const resetUserQuotaByFeatureCodeAPI = (userId: number, featureCode: string) => {
  return httpRequest.post<UserQuota>({
    url: API.resetUserQuotaByFeatureCode.replace(':userId', userId.toString()),
    params: { feature_code: featureCode },
  });
};
